import { ChecklistScope, TaskCompletionMode } from 'generated/graphql';
import { Checklist, ChecklistTask, Strategy, TaskAction } from './Guide.types';

type GQLTask = {
  id: string;
  title: string;
  subtitle?: string | null;
  actionUrl?: string | null;
  actionModal?: string | null;
  actionPage?: string | null;
  estimatedTimeLabel?: string | null;
  completionMode: TaskCompletionMode;
  isChecked: boolean;
  completedAt?: string | null;
  completedBy?: string | null;
};

type GQLChecklist = {
  id: string;
  title: string;
  description?: string | null;
  estimatedDurationLabel?: string | null;
  scope: ChecklistScope;
  strategyIds: string[];
  isActive: boolean;
  tasks: GQLTask[];
};

type GQLStrategy = {
  id: string;
  name: string;
  isActive: boolean;
  isSelected?: boolean | null;
  launchTimeLabel?: string | null;
};

const mapTaskAction = (raw: GQLTask): TaskAction => {
  if (raw.actionModal) return { type: 'modal', templateId: raw.actionModal };
  if (raw.actionPage) return { type: 'page', pageKey: raw.actionPage };
  if (raw.actionUrl) return { type: 'external', url: raw.actionUrl };
  return { type: 'none' };
};

export const mapTask = (raw: GQLTask): ChecklistTask => ({
  id: raw.id,
  title: raw.title,
  subtitle: raw.subtitle ?? null,
  action: mapTaskAction(raw),
  estimatedTimeLabel: raw.estimatedTimeLabel ?? null,
  completionMode: raw.completionMode,
  isChecked: raw.isChecked,
  completedAt: raw.completedAt ?? null,
  completedBy: raw.completedBy ?? null,
});

export const mapChecklist = (raw: GQLChecklist): Checklist => ({
  id: raw.id,
  title: raw.title,
  description: raw.description ?? null,
  estimatedDurationLabel: raw.estimatedDurationLabel ?? null,
  scope: raw.scope,
  strategyIds: raw.strategyIds,
  isActive: raw.isActive,
  tasks: raw.tasks.map(mapTask),
});

export const mapChecklists = (raw: GQLChecklist[]): Checklist[] =>
  raw.map(mapChecklist);

export const mapStrategy = (
  raw: GQLStrategy[],
  selectedStrategyId: string | null,
): Strategy | null => {
  const selectedStrategy = selectedStrategyId
    ? raw.find((s) => s.id === selectedStrategyId)
    : null;
  const activeStrategy = selectedStrategy ?? raw.find((s) => s.isSelected);
  if (!activeStrategy) return null;
  return {
    id: activeStrategy.id,
    name: activeStrategy.name,
    isActive: activeStrategy.isSelected ?? false,
    launchTimeLabel: activeStrategy.launchTimeLabel ?? null,
  };
};
